import React, { useCallback, useContext, useState, useEffect, useRef } from 'react';
import { isNumber } from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Button } from '@fluentui/react-components';
import { getAdPlacementComponent } from '@bingads-webui-campaign-react/unified-ad-preview-library-core';

import { ChevronUpIcon, ChevronDownIcon } from './icons';
import { VerticalProgressBar } from './vertical-progress-bar';
import { getAutoPlayLayoutStyles } from './auto-play-layout-styles';

const MAX_PROGRESS = 50;

export const AutoPlayLayout = ({
  placementList,
  onRenderPlacement,
  onCurrentPlacementChange,
  customPlacementIndex,
}) => {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getAutoPlayLayoutStyles);
  const [currentPlacementIndex, setCurrentPlacementIndex] = useState(0);
  const [timer, setTimer] = useState(0);
  const timerRef = useRef(null);
  const timeoutRef = useRef(null);

  const [enableAnimationIn, setEnableAnimationIn] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  useEffect(() => {
    if (onCurrentPlacementChange) {
      const currentPlacement = placementList[currentPlacementIndex];
      const { placementMeta } = currentPlacement || {};
      const { placementType } = placementMeta || {};
      onCurrentPlacementChange(currentPlacementIndex, placementType, currentPlacement);
    }
  }, [currentPlacementIndex, placementList, onCurrentPlacementChange]);

  const stopTimer = useCallback(() => {
    if (timerRef && timerRef.current) {
      clearInterval(timerRef.current);
    }
    setTimer(0);
  }, []);

  const startTimer = useCallback(() => {
    if (timerRef && timerRef.current) {
      clearInterval(timerRef.current);
    }
    timerRef.current = setInterval(() => {
      setTimer(value => (value < MAX_PROGRESS ? value + 1 : MAX_PROGRESS));
    }, 100);
  }, []);

  const onCurrentPlacementSelected = useCallback((index) => {
    if (index >= 0 && index < placementList.length) {
      setCurrentPlacementIndex(index);
    }
    // Restart timer
    stopTimer();
    startTimer();
  }, [placementList.length, startTimer, stopTimer]);

  const scrollToLastPlacement = useCallback(() => {
    setCurrentPlacementIndex((index) => {
      const lastFeatureIndex = index - 1;
      return lastFeatureIndex < 0 ? placementList.length - 1 : lastFeatureIndex;
    });
    // Restart timer
    stopTimer();
    startTimer();
  }, [placementList.length, startTimer, stopTimer]);

  const scrollToNextPlacement = useCallback(() => {
    setCurrentPlacementIndex((index) => {
      const nextFeatureIndex = index + 1;
      return nextFeatureIndex >= placementList.length ? 0 : nextFeatureIndex;
    });
    // Restart timer
    stopTimer();
    startTimer();
  }, [placementList.length, startTimer, stopTimer]);

  useEffect(() => {
    if (isNumber(customPlacementIndex)) {
      onCurrentPlacementSelected(customPlacementIndex);
    }
    // Only listen to customPlacementIndex change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customPlacementIndex]);

  useEffect(() => {
    if (timer === MAX_PROGRESS) {
      if (!enableAnimationIn) {
        setEnableAnimationIn(true);
      }

      setIsAnimatingOut(true);
      timeoutRef.current = setTimeout(() => {
        scrollToNextPlacement();
        setIsAnimatingOut(false);
      }, 500);
    }

    return () => {
      if (timeoutRef && timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        setIsAnimatingOut(false);
      }
    };
  }, [scrollToNextPlacement, timer, enableAnimationIn, setEnableAnimationIn, setIsAnimatingOut]);

  useEffect(() => {
    startTimer();
  }, [startTimer]);

  useEffect(() => () => {
    stopTimer();
  }, [stopTimer]);

  return (
    <div className={classNames('auto-play-layout', classes.layoutContainer)}>
      <div className={classNames('auto-play-placement', classes.placementContainer)}>
        {placementList.map((placement, index) => {
          const { placementMeta } = placement || {};
          const { placementType } = placementMeta || {};
          const AdPlacementComponent = getAdPlacementComponent(placementType);

          const isActive = index === currentPlacementIndex;

          let placementClassName = classes.activePlacement;
          if (!isActive) {
            placementClassName = classes.hiddenPlacement;
          } else if (isAnimatingOut) {
            placementClassName = classes.animationOutPlacement;
          } else if (enableAnimationIn) {
            placementClassName = classes.animationInPlacement;
          }

          if (AdPlacementComponent) {
            return (
              <div
                key={`key-${placementMeta ? placementMeta.placementType : index}`}
                className={
                  classNames('placement-item-container', placementMeta.placementType, placementClassName)
                }
              >
                {onRenderPlacement(placement, AdPlacementComponent)}
              </div>
            );
          }
          return null;
        })}
      </div>
      <div className={classNames('auto-play-slide', classes.slideContainer)}>
        <Button appearance="transparent" icon={<ChevronUpIcon />} onClick={scrollToLastPlacement} aria-label={i18n.getString(_TL_('go to previous'))} />
        {placementList.map((placement, index) => {
          const isActive = index === currentPlacementIndex;
          const { placementMeta } = placement || {};

          return (
            <div
              key={`key-${placementMeta ? placementMeta.placementType : index}`}
              className={classNames('auto-play-slide-progress', isActive ? classes.activeProgressBar : classes.progressBar)}
              role="button"
              onClick={() => onCurrentPlacementSelected(index)}
              onKeyDown={() => onCurrentPlacementSelected(index)}
              tabIndex={0}
            >
              <VerticalProgressBar
                max={MAX_PROGRESS}
                value={isActive ? timer : 0}
                index={index}
              />
            </div>
          );
        })}
        <Button appearance="transparent" icon={<ChevronDownIcon />} onClick={scrollToNextPlacement} aria-label={i18n.getString(_TL_('go to next'))} />
      </div>
    </div>
  );
};

AutoPlayLayout.propTypes = {
  placementList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onRenderPlacement: PropTypes.func.isRequired,
  onCurrentPlacementChange: PropTypes.func,
  customPlacementIndex: PropTypes.number,
};

AutoPlayLayout.defaultProps = {
  onCurrentPlacementChange: undefined,
  customPlacementIndex: undefined,
};
